<template>
  <BasicNavbar>
    <template #action>
      <FloatingButton :class="skipClasses" @click="handleSkip">
        Skip
      </FloatingButton>
    </template>
  </BasicNavbar>
  <div
    class="w-full flex justify-center px-4 pb-8 mt-[30px] tablet-v:mt-[56px] tablet-h:mt-[90px]"
  >
    <div class="w-full max-w-[410px]">
      <div class="mb-10">
        <h1
          class="text-[26px] font-medium leading-[30px] tracking-[-0.325px] text-gray-800"
        >
          Add organization?
        </h1>
      </div>

      <p class="mb-[14px] text-[14px] leading-[20px] text-gray-800">
        Add an organization if you plan to add work for copyright protection on
        their behalf.
      </p>

      <div class="mb-4">
        <OrganizationSearch
          v-model="selectedOrg"
          label="Organization name (optional)"
          :disabled="loading"
          @update:query="handleNameUpdate"
        />
      </div>

      <Button
        class="w-full"
        variant="primary"
        size="lg"
        :loading="loading"
        :disabled="!hasOrg"
        @click="handleSubmit"
      >
        Get started
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import BasicNavbar from '@/shared/components/BasicNavbar.vue'
import OrganizationSearch from '@/features/search/components/OrganizationSearch.vue'
import { Button, FloatingButton } from '@dacr/library'
import {
  CopyrightOwnersNewDocument,
  type CopyrightOwnersNewQuery,
  CopyrightOwnerType,
  EntityType,
  type Organization,
  useAddCopyrightOwnerEntityMutation,
  useAddCopyrightOwnerMutation,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { pricingCheckoutEntryRoute } from '@/features/onboarding/usePricingCheckoutResolver'

const { mutate: addCopyrightOwner, loading: loadingAddCoprightOwner } =
  useAddCopyrightOwnerMutation()
const {
  mutate: addCopyrightOwnerEntity,
  loading: loadingAddCoprightOwnerEntity,
} = useAddCopyrightOwnerEntityMutation()

const router = useRouter()
const toastStore = useToastStore()
const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const organizationName = ref('')
const selectedOrg = ref<Organization>()
const errorMessage = ref('')

const loading = computed(
  () => loadingAddCoprightOwner.value || loadingAddCoprightOwnerEntity.value
)

const hasOrg = computed(
  () => !!organizationName.value.trim() || !!selectedOrg.value
)

const skipClasses = computed(() => ({
  ...(loading.value && {
    'opacity-50': true,
    'pointer-events-none': true,
  }),
}))

const nextRoute = computed(() =>
  originFlagEnabled.value ? '/register/identity' : pricingCheckoutEntryRoute()
)

const handleSkip = () => {
  router.push(nextRoute.value)
}

const handleDone = () => {
  window.scrollTo(0, 0)
}

const handleNameUpdate = (value: string) => {
  organizationName.value = value
}

const handleSubmit = async () => {
  handleDone()

  if (!organizationName.value && !selectedOrg.value) {
    errorMessage.value = 'Please enter an organization name'
    return
  }

  try {
    if (organizationName.value) {
      await addCopyrightOwner(
        {
          name: organizationName.value,
          type: CopyrightOwnerType.Organization,
        },
        {
          update(cache, { data }) {
            cache.updateQuery<CopyrightOwnersNewQuery>(
              {
                query: CopyrightOwnersNewDocument,
              },
              (response) => {
                if (!response || !data) {
                  return
                }

                if (!response && data) {
                  return {
                    __typename: 'Query',
                    copyrightOwnersNew: {
                      __typename: 'CopyrightOwnersResponseNew',
                      items: [
                        {
                          __typename: 'CopyrightOwnerNew',
                          id: Date.now().toString(),
                          name: data.addCopyrightOwnerNew.name,
                          avatarUrl: data.addCopyrightOwnerNew.avatarUrl,
                          isSelected: data.addCopyrightOwnerNew.isSelected,
                          type: data.addCopyrightOwnerNew.type,
                          owner: data.addCopyrightOwnerNew.owner,
                        },
                      ],
                      total: 1,
                      next: null,
                    },
                  }
                }

                return {
                  ...response,
                  copyrightOwnersNew: {
                    ...response.copyrightOwnersNew,
                    items: response.copyrightOwnersNew.items.concat({
                      __typename: 'CopyrightOwnerNew',
                      id: Date.now().toString(),
                      name: data.addCopyrightOwnerNew.name,
                      avatarUrl: data.addCopyrightOwnerNew.avatarUrl,
                      isSelected: data.addCopyrightOwnerNew.isSelected,
                      type: data.addCopyrightOwnerNew.type,
                      owner: data.addCopyrightOwnerNew.owner,
                    }),
                  },
                }
              }
            )
          },
        }
      )
    }

    if (selectedOrg.value) {
      await addCopyrightOwnerEntity(
        {
          input: {
            id: selectedOrg.value.id,
            type: EntityType.Organization,
          },
        },
        {
          update(cache, { data }) {
            cache.updateQuery<CopyrightOwnersNewQuery>(
              {
                query: CopyrightOwnersNewDocument,
              },
              (response) => {
                if (!response || !data) {
                  return
                }

                if (!response && data) {
                  return {
                    __typename: 'Query',
                    copyrightOwnersNew: {
                      __typename: 'CopyrightOwnersResponseNew',
                      items: [
                        {
                          __typename: 'CopyrightOwnerNew',
                          id: Date.now().toString(),
                          name: data.addCopyrightOwnerEntityNew.name,
                          avatarUrl: data.addCopyrightOwnerEntityNew.avatarUrl,
                          isSelected:
                            data.addCopyrightOwnerEntityNew.isSelected,
                          type: data.addCopyrightOwnerEntityNew.type,
                          owner: data.addCopyrightOwnerEntityNew.owner,
                        },
                      ],
                      total: 1,
                      next: null,
                    },
                  }
                }

                return {
                  ...response,
                  copyrightOwnersNew: {
                    ...response.copyrightOwnersNew,
                    items: response.copyrightOwnersNew.items.concat({
                      __typename: 'CopyrightOwnerNew',
                      id: Date.now().toString(),
                      name: data.addCopyrightOwnerEntityNew.name,
                      avatarUrl: data.addCopyrightOwnerEntityNew.avatarUrl,
                      isSelected: data.addCopyrightOwnerEntityNew.isSelected,
                      type: data.addCopyrightOwnerEntityNew.type,
                      owner: data.addCopyrightOwnerEntityNew.owner,
                    }),
                  },
                }
              }
            )
          },
        }
      )
    }
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later.',
      error: error as Error,
    })
  }

  return router.push(nextRoute.value)
}
</script>
